import { useCallback, useMemo, useRef } from "react";
import type { Ad } from "@/app/components/ad-card";
import type { Address } from "@/app/components/checkout-address-page";
import type { PaymentMethod } from "@/app/components/checkout-payment-page";
import type { Conversation } from "@/app/components/chat-list-page";
import type { CartItem } from "@/app/components/cart-page";
import type {
  ChatMessage,
  PageType,
  ReceivedOffer,
  SentOffer,
} from "./use-app-state";
import type { OnboardingData } from "@/app/components/new-user-onboarding";
import * as authClient from "@/app/api/auth-client";
import { clearSessionAppRoute } from "@/app/utils/session-app-route";

/** P2: freight audit from quote option (post-malone); do not recompute markup client-side. */
function freightAuditFromChosenOption(
  chosen: {
    price: number;
    sellerAmount?: number;
    rawPriceCents?: number;
    freightMarkupCents?: number;
    buyerRawPriceCents?: number;
    buyerFreightMarkupCents?: number;
    sellerRawPriceCents?: number;
    sellerFreightMarkupCents?: number;
  },
  isSwapCart: boolean
): Partial<
  Pick<
    authClient.CreateOrderRequest,
    | "shipping_raw_cents"
    | "shipping_freight_markup_cents"
    | "second_party_shipping_raw_cents"
    | "second_party_shipping_freight_markup_cents"
  >
> {
  const shippingCents = Math.round(chosen.price * 100);
  if (!isSwapCart) {
    if (chosen.rawPriceCents != null && chosen.freightMarkupCents != null) {
      if (chosen.rawPriceCents + chosen.freightMarkupCents !== shippingCents) {
        console.warn("[checkout] P2: shipping_cents !== raw+markup", {
          shippingCents,
          raw: chosen.rawPriceCents,
          markup: chosen.freightMarkupCents,
        });
      }
      return {
        shipping_raw_cents: chosen.rawPriceCents,
        shipping_freight_markup_cents: chosen.freightMarkupCents,
      };
    }
    // Stale BFF shipping cache (pre–P2 shape) or proxy without audit fields: still persist keys on the order.
    console.warn(
      "[checkout] P2: missing raw_price_cents/freight_markup_cents on shipping option; persisting shipping as raw, markup 0 (audit may be incomplete if platform markup applied)"
    );
    return {
      shipping_raw_cents: shippingCents,
      shipping_freight_markup_cents: 0,
    };
  }
  const out: Partial<
    Pick<
      authClient.CreateOrderRequest,
      | "shipping_raw_cents"
      | "shipping_freight_markup_cents"
      | "second_party_shipping_raw_cents"
      | "second_party_shipping_freight_markup_cents"
    >
  > = {};
  if (chosen.buyerRawPriceCents != null && chosen.buyerFreightMarkupCents != null) {
    if (chosen.buyerRawPriceCents + chosen.buyerFreightMarkupCents !== shippingCents) {
      console.warn("[checkout] P2 (hub): buyer shipping !== raw+markup", {
        shippingCents,
        raw: chosen.buyerRawPriceCents,
        markup: chosen.buyerFreightMarkupCents,
      });
    }
    out.shipping_raw_cents = chosen.buyerRawPriceCents;
    out.shipping_freight_markup_cents = chosen.buyerFreightMarkupCents;
  }
  if (
    chosen.sellerAmount != null &&
    chosen.sellerRawPriceCents != null &&
    chosen.sellerFreightMarkupCents != null
  ) {
    const sp = Math.round(chosen.sellerAmount * 100);
    if (chosen.sellerRawPriceCents + chosen.sellerFreightMarkupCents !== sp) {
      console.warn("[checkout] P2 (hub): second_party !== raw+markup", {
        secondPartyCents: sp,
        raw: chosen.sellerRawPriceCents,
        markup: chosen.sellerFreightMarkupCents,
      });
    }
    out.second_party_shipping_raw_cents = chosen.sellerRawPriceCents;
    out.second_party_shipping_freight_markup_cents = chosen.sellerFreightMarkupCents;
  }
  if (out.shipping_raw_cents == null && out.shipping_freight_markup_cents == null) {
    console.warn(
      "[checkout] P2 (hub): missing buyer raw/markup on option; persisting buyer shipping as raw, markup 0"
    );
    out.shipping_raw_cents = shippingCents;
    out.shipping_freight_markup_cents = 0;
  }
  if (
    chosen.sellerAmount != null &&
    out.second_party_shipping_raw_cents == null &&
    out.second_party_shipping_freight_markup_cents == null
  ) {
    const sp = Math.round(chosen.sellerAmount * 100);
    console.warn(
      "[checkout] P2 (hub): missing second-party raw/markup; persisting sellerAmount as raw, markup 0"
    );
    out.second_party_shipping_raw_cents = sp;
    out.second_party_shipping_freight_markup_cents = 0;
  }
  return out;
}

interface AppState {
  ads: Ad[];
  setAds: (ads: Ad[] | ((prev: Ad[]) => Ad[])) => void;
  favoriteItems: any[];
  setFavoriteItems: (items: any[] | ((prev: any[]) => any[])) => void;
  favoriteAdIds: string[];
  setFavoriteAdIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  favoriteCreatedAt: Record<string, string>;
  setFavoriteCreatedAt: (m: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  cart: any[];
  setCart: (cart: any[] | ((prev: any[]) => any[])) => void;
  setCartToastProduct: (product: string) => void;
  setShowCartToast: (show: boolean) => void;
  setCartBlockMessage: (message: string | null) => void;
  addresses: Address[];
  setAddresses: (addresses: Address[] | ((prev: Address[]) => Address[])) => void;
  selectedAddressId: string | null;
  setSelectedAddressId: (id: string | null) => void;
  selectedShippingId: string | null;
  setSelectedShippingId: (id: string | null) => void;
  selectedPaymentId: string | null;
  setSelectedPaymentId: (id: string | null) => void;
  selectedInstallments: number;
  setSelectedInstallments: (installments: number) => void;
  paymentMethods: PaymentMethod[];
  setPaymentMethods: (methods: PaymentMethod[] | ((prev: PaymentMethod[]) => PaymentMethod[])) => void;
  setLastOrderNumber: (number: string) => void;
  orderError: string | null;
  setOrderError: (err: string | null) => void;
  orderConfirming: boolean;
  setOrderConfirming: (v: boolean) => void;
  shippingOptions: Array<{
    id: string;
    name: string;
    price: number;
    sellerAmount?: number;
    leg1?: number;
    leg1_1?: number;
    leg2?: number;
    leg2_1?: number;
    rawPriceCents?: number;
    freightMarkupCents?: number;
    buyerRawPriceCents?: number;
    buyerFreightMarkupCents?: number;
    sellerRawPriceCents?: number;
    sellerFreightMarkupCents?: number;
  }>;
  setShippingOptions: (options: Array<{
    id: string;
    name: string;
    description: string;
    estimatedDays: string;
    price: number;
    type: "standard" | "express";
    sellerAmount?: number;
    leg1?: number;
    leg1_1?: number;
    leg2?: number;
    leg2_1?: number;
    rawPriceCents?: number;
    freightMarkupCents?: number;
    buyerRawPriceCents?: number;
    buyerFreightMarkupCents?: number;
    sellerRawPriceCents?: number;
    sellerFreightMarkupCents?: number;
  }>) => void;
  setShippingOptionsLoading: (loading: boolean) => void;
  setShippingOptionsError: (msg: string | null) => void;
  hubDisplay: { city: string; state: string } | null;
  setHubDisplay: (hub: { city: string; state: string } | null) => void;
  pendingPaymentOrderId: string | null;
  pendingPaymentClientSecret: string | null;
  setCurrentPage: (page: PageType) => void;
  setReturnToPage: (page: PageType | null) => void;
  setPendingPaymentOrderId: (id: string | null) => void;
  setPendingPaymentClientSecret: (secret: string | null) => void;
  setPendingPaymentPublishableKey: (key: string) => void;
  secondPartyPaymentOrderId?: string | null;
  setSecondPartyPaymentOrderId?: (id: string | null) => void;
  payOrderId?: string | null;
  setPayOrderId?: (id: string | null) => void;
  cartOrderId?: string | null;
  setCartOrderId?: (id: string | null) => void;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => void;
  setSelectedConversationId: (id: string | null) => void;
  selectedConversationId: string | null;
  userId: string;
  setIsChatModalOpen: (open: boolean) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
  setUserId: (id: string) => void;
  setBrowsingAfterSignup: (value: boolean) => void;
  setIsMenuOpen: (open: boolean) => void;
  setSelectedSellerName: (name: string) => void;
  setSelectedSellerId: (id: string | null) => void;
  setNavigatedFromMenu: (navigated: boolean) => void;
  /** User display name (for recipient on order/label and quote). */
  userName?: string;
  /** User profile (e.g. phone for recipient). */
  userProfile?: { phone?: string } | null;
  setUserProfile: (profile: OnboardingData | null) => void;
  setUserProfilePicture: (url: string) => void;
  setUserEmail: (email: string) => void;
  setUserName: (name: string) => void;
  setAuthEmailVerified: (v: boolean | undefined) => void;
  setNeedsOnboarding: (v: boolean) => void;
  setChatMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setNotificationUnreadCount: (n: number) => void;
  setNavigationHistory: (h: PageType[] | ((prev: PageType[]) => PageType[])) => void;
  setReceivedOffers: (
    offers: ReceivedOffer[] | ((prev: ReceivedOffer[]) => ReceivedOffer[])
  ) => void;
  setSentOffers: (offers: SentOffer[] | ((prev: SentOffer[]) => SentOffer[])) => void;
}

interface Navigation {
  navigateToPage: (page: PageType) => void;
  navigateBack: () => void;
}

export function useAppHandlers(state: AppState, navigation?: Navigation) {
  // Keep latest hub display without listing it in loadShippingOptions deps — otherwise setHubDisplay
  // from getHubDisplay() recreates the callback and app-router's effect POSTs shipping-options again (429).
  const hubDisplayRef = useRef(state.hubDisplay);
  hubDisplayRef.current = state.hubDisplay;

  const shippingLoadGenerationRef = useRef(0);

  const handleCreateAd = useCallback((payload: Ad | Omit<Ad, "id" | "createdAt">) => {
    const ad: Ad =
      "id" in payload && payload.id
        ? (payload as Ad)
        : {
            ...(payload as Omit<Ad, "id" | "createdAt">),
            id: Date.now().toString(),
            createdAt: "Agora mesmo",
          };
    state.setAds((prev) => [ad, ...prev]);
  }, [state.setAds]);

  /** Toggle favorite via API. When not logged in, redirect to signup/login (no temporary favorites). */
  const handleToggleFavorite = useCallback((adId: string) => {
    if (!authClient.getAccessToken()) {
      state.setBrowsingAfterSignup(false);
      return;
    }
    const currentlyFavorite = state.favoriteAdIds.includes(adId);
    if (currentlyFavorite) {
      state.setFavoriteAdIds((prev) => prev.filter((id) => id !== adId));
      state.setFavoriteCreatedAt((prev) => {
        const next = { ...prev };
        delete next[adId];
        return next;
      });
      authClient.removeFavorite(adId).catch(() => {
        state.setFavoriteAdIds((prev) => [...prev, adId]);
        state.setFavoriteCreatedAt((prev) => ({ ...prev, [adId]: new Date().toISOString() }));
      });
    } else {
      state.setFavoriteAdIds((prev) => [...prev, adId]);
      state.setFavoriteCreatedAt((prev) => ({ ...prev, [adId]: new Date().toISOString() }));
      authClient.addFavorite(adId).catch(() => {
        state.setFavoriteAdIds((prev) => prev.filter((id) => id !== adId));
        state.setFavoriteCreatedAt((prev) => {
          const next = { ...prev };
          delete next[adId];
          return next;
        });
      });
    }
  }, [state.favoriteAdIds, state.setFavoriteAdIds, state.setFavoriteCreatedAt, state.setBrowsingAfterSignup]);

  /** Remove favorite via API. When not logged in, redirect to signup/login. */
  const handleRemoveFavorite = useCallback((adId: string) => {
    if (!authClient.getAccessToken()) {
      state.setBrowsingAfterSignup(false);
      return;
    }
    state.setFavoriteAdIds((prev) => prev.filter((id) => id !== adId));
    state.setFavoriteCreatedAt((prev) => {
      const next = { ...prev };
      delete next[adId];
      return next;
    });
    authClient.removeFavorite(adId).catch(() => {
      state.setFavoriteAdIds((prev) => [...prev, adId]);
      state.setFavoriteCreatedAt((prev) => ({ ...prev, [adId]: new Date().toISOString() }));
    });
  }, [state.setFavoriteAdIds, state.setFavoriteCreatedAt, state.setBrowsingAfterSignup]);

  const isFavorite = useCallback((adId: string) => {
    return state.favoriteAdIds.includes(adId);
  }, [state.favoriteAdIds]);

  const cartItemsToApi = useCallback(
    (items: Array<{ ad: { id: string }; quantity: number; offerId?: string; offerPrice?: number }>): authClient.CartItemApi[] =>
      items.map((it) => ({
        ad_id: it.ad.id,
        quantity: it.quantity,
        ...(it.offerId && { offer_id: it.offerId }),
        ...(it.offerPrice != null && { agreed_price_cents: Math.round(it.offerPrice * 100) }),
      })),
    []
  );

  const loadShippingOptions = useCallback(
    async (
      cep: string,
      items: Array<{ ad_id: string; quantity: number }>,
      opts?: { cartOverride?: Array<{ isOfferItem?: boolean; offerId?: string; ad?: { id: string; shipping_origin_cep?: string }; offerTradeInAds?: Array<{ id: string }> }> }
    ) => {
      if (!cep || items.length === 0) return [];
      const loadId = ++shippingLoadGenerationRef.current;
      state.setShippingOptionsError(null);
      state.setShippingOptionsLoading(true);
      try {
        const recipientName = state.userName?.trim() ?? "";
        const recipientPhone = state.userProfile?.phone?.trim() ?? "";
        const baseOptions = { recipient_name: recipientName, recipient_phone: recipientPhone };

        // Swap (hub routing): when cart has an offer item with trade-ins, request four-leg quote so buyer sees Leg 1.1 + Leg 2 (buyer_amount).
        const cart = (opts?.cartOverride ?? state.cart) as Array<{ isOfferItem?: boolean; offerId?: string; ad?: { id: string; shipping_origin_cep?: string }; offerTradeInAds?: Array<{ id: string }> }>;
        const offerItem = cart?.find((i) => i.isOfferItem);
        // Preload hub display so the route card can render first (before options come back).
        if (offerItem && !hubDisplayRef.current) {
          authClient.getHubDisplay().then((hub) => hub && state.setHubDisplay(hub));
        }
        let hubParams: authClient.ShippingOptionsHubParams | undefined;
        if (offerItem?.ad) {
          let tradeInIds = offerItem.offerTradeInAds?.map((t) => t.id) ?? [];
          if (tradeInIds.length === 0 && offerItem.offerId) {
            try {
              const offerApi = await authClient.getOfferById(offerItem.offerId);
              if (offerApi.product_ad_ids?.length) tradeInIds = offerApi.product_ad_ids;
            } catch {
              // ignore
            }
          }
          let sellerDestinationCep = (offerItem.ad.shipping_origin_cep ?? "").replace(/\D/g, "").slice(0, 8);
          if (sellerDestinationCep.length !== 8 && offerItem.ad.id) {
            try {
              const adApi = await authClient.getAdById(offerItem.ad.id);
              sellerDestinationCep = (adApi.shipping_origin_cep ?? "").replace(/\D/g, "").slice(0, 8);
            } catch {
              // keep previous
            }
          }
          const buyerOriginCep = cep.replace(/\D/g, "").slice(0, 8);
          if (tradeInIds.length > 0 && buyerOriginCep.length === 8 && sellerDestinationCep.length === 8) {
            const buyerProducts = await Promise.all(
              tradeInIds.map(async (id) => {
                try {
                  const api = await authClient.getAdById(id);
                  const ad = authClient.apiAdToAd(api);
                  const w = ad.shipping_width_cm ?? 16;
                  const h = ad.shipping_height_cm ?? 11;
                  const l = ad.shipping_length_cm ?? 11;
                  const weight = ad.shipping_weight_kg ?? 1;
                  const insuranceValue = Math.max(0.01, ad.price ?? 0);
                  return { id: ad.id, width: w, height: h, length: l, weight, insurance_value: insuranceValue, quantity: 1 };
                } catch {
                  return { id, width: 16, height: 11, length: 11, weight: 1, insurance_value: 0.01, quantity: 1 };
                }
              })
            );
            hubParams = {
              use_hub_routing: true,
              buyer_origin_cep: buyerOriginCep,
              seller_destination_cep: sellerDestinationCep,
              buyer_products: buyerProducts,
            };
          }
        }

        const options = await authClient.getShippingOptions(cep, items, {
          ...baseOptions,
          ...(hubParams && { hubParams }),
        });
        if (loadId !== shippingLoadGenerationRef.current) return options;
        state.setShippingOptions(options);
        state.setSelectedShippingId(options[0]?.id ?? null);
        if (hubParams && !hubDisplayRef.current) {
          authClient.getHubDisplay().then((hub) => hub && state.setHubDisplay(hub));
        }
        return options;
      } catch (err: unknown) {
        if (loadId === shippingLoadGenerationRef.current) {
          state.setShippingOptions([]);
          state.setSelectedShippingId(null);
          const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : null;
          state.setShippingOptionsError(msg || "Não foi possível carregar as opções de envio.");
        }
      } finally {
        if (loadId === shippingLoadGenerationRef.current) {
          state.setShippingOptionsLoading(false);
        }
      }
      return [];
    },
    [state.cart, state.userName, state.userProfile, state.setShippingOptions, state.setShippingOptionsLoading, state.setShippingOptionsError, state.setSelectedShippingId, state.setHubDisplay]
  );

  const handleAddToCart = useCallback(
    (adOrCartItem: string | any): boolean => {
      const cart = state.cart;
      const hasOfferItem = cart.some((item) => item.isOfferItem === true);

      if (typeof adOrCartItem === "object" && adOrCartItem.ad) {
        const cartItem = adOrCartItem;
        const isNewOffer = cartItem.isOfferItem === true;
        if (isNewOffer) {
          if (cart.length > 0) {
            state.setCartBlockMessage(
              "Finalize ou esvazie seu carrinho antes de adicionar outra oferta. Remova os itens atuais ou conclua a compra e tente novamente."
            );
            return false;
          }
        } else {
          if (hasOfferItem) {
            state.setCartBlockMessage(
              "Finalize ou esvazie seu carrinho antes de adicionar um anúncio. No momento o carrinho contém uma oferta; ele só pode ter uma oferta ou apenas anúncios comuns."
            );
            return false;
          }
        }
      } else {
        const adId = adOrCartItem as string;
        if (hasOfferItem) {
          state.setCartBlockMessage(
            "Finalize ou esvazie seu carrinho antes de adicionar um anúncio. No momento o carrinho contém uma oferta; ele só pode ter uma oferta ou apenas anúncios comuns."
          );
          return false;
        }
        const ad = state.ads.find((a) => a.id === adId);
        if (!ad) return false;
      }

      let nextCart: typeof state.cart;
      if (typeof adOrCartItem === "object" && adOrCartItem.ad) {
        const cartItem = adOrCartItem;
        nextCart = [...state.cart, cartItem];
        state.setCartToastProduct(cartItem.isOfferItem ? "__OFFER__" : cartItem.ad.title);
        state.setShowCartToast(true);
      } else {
        const adId = adOrCartItem as string;
        const ad = state.ads.find((a) => a.id === adId)!;
        nextCart = [...state.cart, { ad, quantity: 1 }];
        state.setCartToastProduct(ad.title);
        state.setShowCartToast(true);
      }
      state.setCart(nextCart);

      // No linked order: just update cart and clear shipping
      if (state.cartOrderId == null) {
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        state.setHubDisplay(null);
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        return true;
      }

      // Linked order: re-quote shipping and patch order so review shows new items + shipping
      const selectedAddress = state.addresses.find((a) => a.id === state.selectedAddressId);
      if (!state.selectedAddressId || !selectedAddress) {
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        state.setHubDisplay(null);
        if (state.setCartOrderId) state.setCartOrderId(null);
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        return true;
      }
      state.setShippingOptions([]);
      state.setSelectedShippingId(null);
      const cartOrderId = state.cartOrderId;
      const previousShippingId = state.selectedShippingId;
      (async () => {
        const cep = (selectedAddress.zipCode ?? "").replace(/\D/g, "").slice(0, 8);
        if (!cep) {
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          return;
        }
        const itemsForQuote = nextCart.map((it) => ({ ad_id: it.ad.id, quantity: it.quantity }));
        const options = (await loadShippingOptions(cep, itemsForQuote, { cartOverride: nextCart })) ?? [];
        if (!options.length) {
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          return;
        }
        const chosen = options.find((o) => o.id === previousShippingId) ?? options[0];
        state.setSelectedShippingId(chosen.id);
        const recipientName = state.userName?.trim() ?? "";
        const recipientPhone = state.userProfile?.phone?.trim() ?? "";
        const address_snapshot: authClient.OrderAddressSnapshot = {
          cep: selectedAddress.zipCode,
          street: selectedAddress.street,
          number: selectedAddress.number,
          complement: selectedAddress.complement ?? "",
          neighborhood: selectedAddress.neighborhood,
          city: selectedAddress.city,
          state: selectedAddress.state,
          recipient_name: recipientName,
          recipient_phone: recipientPhone,
        };
        const isSwapCart = nextCart.some((it) => it.isOfferItem === true);
        const leg1Cents = chosen.leg1 != null && chosen.leg1 > 0 ? Math.round(chosen.leg1 * 100) : undefined;
        const leg11Cents = chosen.leg1_1 != null && chosen.leg1_1 > 0 ? Math.round(chosen.leg1_1 * 100) : undefined;
        const leg2Cents = chosen.leg2 != null && chosen.leg2 > 0 ? Math.round(chosen.leg2 * 100) : undefined;
        const leg21Cents = chosen.leg2_1 != null && chosen.leg2_1 > 0 ? Math.round(chosen.leg2_1 * 100) : undefined;
        const payload: authClient.CreateOrderRequest = {
          items: cartItemsToApi(nextCart),
          address_snapshot,
          shipping_option_id: chosen.id,
          shipping_cents: Math.round(chosen.price * 100),
          ...(isSwapCart && {
            second_party_shipping_cents: Math.round((chosen.sellerAmount ?? 0) * 100),
          }),
          ...(isSwapCart && { deal_type: "swap" }),
          ...(leg1Cents != null && { leg1_quoted_cents: leg1Cents }),
          ...(leg11Cents != null && { leg1_1_quoted_cents: leg11Cents }),
          ...(leg2Cents != null && { leg2_quoted_cents: leg2Cents }),
          ...(leg21Cents != null && { leg2_1_quoted_cents: leg21Cents }),
          ...freightAuditFromChosenOption(chosen, isSwapCart),
        };
        try {
          await authClient.updateOrderContents(cartOrderId, payload);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart), cartOrderId).catch(() => {});
        } catch {
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        }
      })();
      return true;
    },
    [
      state.ads,
      state.cart,
      state.cartOrderId,
      state.addresses,
      state.selectedAddressId,
      state.selectedShippingId,
      state.userName,
      state.userProfile,
      state.setCart,
      state.setShippingOptions,
      state.setSelectedShippingId,
      state.setHubDisplay,
      state.setCartOrderId,
      state.setCartToastProduct,
      state.setShowCartToast,
      state.setCartBlockMessage,
      cartItemsToApi,
      loadShippingOptions,
    ]
  );

  const handleRemoveFromCart = useCallback(
    (adId: string) => {
      const nextCart = state.cart.filter((item) => item.ad.id !== adId);
      const hadLinkedOrder = nextCart.length === 0 && state.cartOrderId != null;
      const orderIdToCancel = hadLinkedOrder ? state.cartOrderId : null;
      state.setCart(nextCart);

      // Last item removed and order linked: cancel order, clear cart link and shipping
      if (nextCart.length === 0) {
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        state.setHubDisplay(null);
        if (state.setCartOrderId) state.setCartOrderId(null);
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        if (orderIdToCancel) authClient.cancelOrder(orderIdToCancel).catch(() => {});
        return;
      }

      // Cart not empty and order linked: re-quote shipping and update order contents (only when we have address + shipping)
      if (state.cartOrderId != null) {
        const selectedAddress = state.addresses.find((a) => a.id === state.selectedAddressId);
        if (!state.selectedAddressId || !selectedAddress) {
          state.setShippingOptions([]);
          state.setSelectedShippingId(null);
          state.setHubDisplay(null);
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          return;
        }
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        const cartOrderId = state.cartOrderId;
        const previousShippingId = state.selectedShippingId;
        (async () => {
          const cep = (selectedAddress.zipCode ?? "").replace(/\D/g, "").slice(0, 8);
          if (!cep) {
            if (state.setCartOrderId) state.setCartOrderId(null);
            if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
            return;
          }
          const itemsForQuote = nextCart.map((it) => ({ ad_id: it.ad.id, quantity: it.quantity }));
          const options = (await loadShippingOptions(cep, itemsForQuote, { cartOverride: nextCart })) ?? [];
          if (!options.length) {
            if (state.setCartOrderId) state.setCartOrderId(null);
            if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
            return;
          }
          const chosen = options.find((o) => o.id === previousShippingId) ?? options[0];
          state.setSelectedShippingId(chosen.id);
          const recipientName = state.userName?.trim() ?? "";
          const recipientPhone = state.userProfile?.phone?.trim() ?? "";
          const address_snapshot: authClient.OrderAddressSnapshot = {
            cep: selectedAddress.zipCode,
            street: selectedAddress.street,
            number: selectedAddress.number,
            complement: selectedAddress.complement ?? "",
            neighborhood: selectedAddress.neighborhood,
            city: selectedAddress.city,
            state: selectedAddress.state,
            recipient_name: recipientName,
            recipient_phone: recipientPhone,
          };
          const isSwapCart = nextCart.some((it) => it.isOfferItem === true);
          const leg1Cents = chosen.leg1 != null && chosen.leg1 > 0 ? Math.round(chosen.leg1 * 100) : undefined;
          const leg11Cents = chosen.leg1_1 != null && chosen.leg1_1 > 0 ? Math.round(chosen.leg1_1 * 100) : undefined;
          const leg2Cents = chosen.leg2 != null && chosen.leg2 > 0 ? Math.round(chosen.leg2 * 100) : undefined;
          const leg21Cents = chosen.leg2_1 != null && chosen.leg2_1 > 0 ? Math.round(chosen.leg2_1 * 100) : undefined;
          const payload: authClient.CreateOrderRequest = {
            items: cartItemsToApi(nextCart),
            address_snapshot,
            shipping_option_id: chosen.id,
            shipping_cents: Math.round(chosen.price * 100),
            ...(isSwapCart && {
              second_party_shipping_cents: Math.round((chosen.sellerAmount ?? 0) * 100),
            }),
            ...(isSwapCart && { deal_type: "swap" }),
            ...(leg1Cents != null && { leg1_quoted_cents: leg1Cents }),
            ...(leg11Cents != null && { leg1_1_quoted_cents: leg11Cents }),
            ...(leg2Cents != null && { leg2_quoted_cents: leg2Cents }),
            ...(leg21Cents != null && { leg2_1_quoted_cents: leg21Cents }),
            ...freightAuditFromChosenOption(chosen, isSwapCart),
          };
          try {
            await authClient.updateOrderContents(cartOrderId, payload);
            if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart), cartOrderId).catch(() => {});
          } catch {
            // Plan §4: clear cartOrderId on any failure (e.g. 400 order already paid, swap first payment done)
            if (state.setCartOrderId) state.setCartOrderId(null);
            if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          }
        })();
        return;
      }

      // No linked order: just update cart and clear shipping
      state.setShippingOptions([]);
      state.setSelectedShippingId(null);
      state.setHubDisplay(null);
      if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
    },
    [
      state.cart,
      state.cartOrderId,
      state.addresses,
      state.selectedAddressId,
      state.selectedShippingId,
      state.userName,
      state.userProfile,
      state.setCart,
      state.setShippingOptions,
      state.setSelectedShippingId,
      state.setHubDisplay,
      state.setCartOrderId,
      cartItemsToApi,
      loadShippingOptions,
    ]
  );

  const isInCart = useCallback((adId: string) => {
    return state.cart.some((item) => item.ad.id === adId);
  }, [state.cart]);

  const handleUpdateCartQuantity = useCallback(
    (adId: string, quantity: number) => {
      const nextCart = state.cart.map((item) =>
        item.ad.id === adId ? { ...item, quantity } : item
      );
      state.setCart(nextCart);

      // Cart empty or no linked order: just update cart and clear shipping
      if (nextCart.length === 0 || state.cartOrderId == null) {
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        state.setHubDisplay(null);
        if (state.cartOrderId != null && state.setCartOrderId) state.setCartOrderId(null);
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        return;
      }

      // Linked order and cart not empty: re-quote shipping and update order contents when we have address
      const selectedAddress = state.addresses.find((a) => a.id === state.selectedAddressId);
      if (!state.selectedAddressId || !selectedAddress) {
        state.setShippingOptions([]);
        state.setSelectedShippingId(null);
        state.setHubDisplay(null);
        if (state.setCartOrderId) state.setCartOrderId(null);
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        return;
      }
      state.setShippingOptions([]);
      state.setSelectedShippingId(null);
      const cartOrderId = state.cartOrderId;
      const previousShippingId = state.selectedShippingId;
      (async () => {
        const cep = (selectedAddress.zipCode ?? "").replace(/\D/g, "").slice(0, 8);
        if (!cep) {
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          return;
        }
        const itemsForQuote = nextCart.map((it) => ({ ad_id: it.ad.id, quantity: it.quantity }));
        const options = (await loadShippingOptions(cep, itemsForQuote, { cartOverride: nextCart })) ?? [];
        if (!options.length) {
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
          return;
        }
        const chosen = options.find((o) => o.id === previousShippingId) ?? options[0];
        state.setSelectedShippingId(chosen.id);
        const recipientName = state.userName?.trim() ?? "";
        const recipientPhone = state.userProfile?.phone?.trim() ?? "";
        const address_snapshot: authClient.OrderAddressSnapshot = {
          cep: selectedAddress.zipCode,
          street: selectedAddress.street,
          number: selectedAddress.number,
          complement: selectedAddress.complement ?? "",
          neighborhood: selectedAddress.neighborhood,
          city: selectedAddress.city,
          state: selectedAddress.state,
          recipient_name: recipientName,
          recipient_phone: recipientPhone,
        };
        const isSwapCart = nextCart.some((it) => it.isOfferItem === true);
        const leg1Cents = chosen.leg1 != null && chosen.leg1 > 0 ? Math.round(chosen.leg1 * 100) : undefined;
        const leg11Cents = chosen.leg1_1 != null && chosen.leg1_1 > 0 ? Math.round(chosen.leg1_1 * 100) : undefined;
        const leg2Cents = chosen.leg2 != null && chosen.leg2 > 0 ? Math.round(chosen.leg2 * 100) : undefined;
        const leg21Cents = chosen.leg2_1 != null && chosen.leg2_1 > 0 ? Math.round(chosen.leg2_1 * 100) : undefined;
        const payload: authClient.CreateOrderRequest = {
          items: cartItemsToApi(nextCart),
          address_snapshot,
          shipping_option_id: chosen.id,
          shipping_cents: Math.round(chosen.price * 100),
          ...(isSwapCart && {
            second_party_shipping_cents: Math.round((chosen.sellerAmount ?? 0) * 100),
          }),
          ...(isSwapCart && { deal_type: "swap" }),
          ...(leg1Cents != null && { leg1_quoted_cents: leg1Cents }),
          ...(leg11Cents != null && { leg1_1_quoted_cents: leg11Cents }),
          ...(leg2Cents != null && { leg2_quoted_cents: leg2Cents }),
          ...(leg21Cents != null && { leg2_1_quoted_cents: leg21Cents }),
          ...freightAuditFromChosenOption(chosen, isSwapCart),
        };
        try {
          await authClient.updateOrderContents(cartOrderId, payload);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart), cartOrderId).catch(() => {});
        } catch {
          // Plan §4: clear cartOrderId on any failure (e.g. 400 order already paid, swap first payment done)
          if (state.setCartOrderId) state.setCartOrderId(null);
          if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(nextCart)).catch(() => {});
        }
      })();
    },
    [
      state.cart,
      state.cartOrderId,
      state.addresses,
      state.selectedAddressId,
      state.selectedShippingId,
      state.userName,
      state.userProfile,
      state.setCart,
      state.setShippingOptions,
      state.setSelectedShippingId,
      state.setHubDisplay,
      state.setCartOrderId,
      cartItemsToApi,
      loadShippingOptions,
    ]
  );

  const handleAddNewAddress = useCallback((address: Omit<Address, "id">) => {
    state.setAddresses(prev => [...prev, { ...address, id: Date.now().toString() }]);
  }, [state.setAddresses]);

  const handleAddNewPaymentMethod = useCallback((payment: Omit<PaymentMethod, "id">) => {
    state.setPaymentMethods(prev => [...prev, { ...payment, id: Date.now().toString() }]);
  }, [state.setPaymentMethods]);

  const handleSelectAddress = useCallback((id: string) => {
    state.setSelectedAddressId(id);
  }, [state.setSelectedAddressId]);

  const handleSelectShipping = useCallback((id: string) => {
    state.setSelectedShippingId(id);
  }, [state.setSelectedShippingId]);

  /** Build create-order payload from current cart, selected address and shipping. Returns null if missing data. */
  const buildOrderContentsPayload = useCallback((): authClient.CreateOrderRequest | null => {
    const selectedAddress = state.addresses.find((a) => a.id === state.selectedAddressId);
    const chosen = state.shippingOptions.find((s) => s.id === state.selectedShippingId);
    if (!selectedAddress || !chosen || state.cart.length === 0) return null;
    const recipientName = state.userName?.trim() ?? "";
    const recipientPhone = state.userProfile?.phone?.trim() ?? "";
    const address_snapshot: authClient.OrderAddressSnapshot = {
      cep: selectedAddress.zipCode,
      street: selectedAddress.street,
      number: selectedAddress.number,
      complement: selectedAddress.complement ?? "",
      neighborhood: selectedAddress.neighborhood,
      city: selectedAddress.city,
      state: selectedAddress.state,
      recipient_name: recipientName,
      recipient_phone: recipientPhone,
    };
    const isSwapCart = state.cart.some((it) => it.isOfferItem === true);
    const leg1Cents = chosen.leg1 != null && chosen.leg1 > 0 ? Math.round(chosen.leg1 * 100) : undefined;
    const leg11Cents = chosen.leg1_1 != null && chosen.leg1_1 > 0 ? Math.round(chosen.leg1_1 * 100) : undefined;
    const leg2Cents = chosen.leg2 != null && chosen.leg2 > 0 ? Math.round(chosen.leg2 * 100) : undefined;
    const leg21Cents = chosen.leg2_1 != null && chosen.leg2_1 > 0 ? Math.round(chosen.leg2_1 * 100) : undefined;
    return {
      items: cartItemsToApi(state.cart),
      address_snapshot,
      shipping_option_id: chosen.id,
      shipping_cents: Math.round(chosen.price * 100),
      ...(isSwapCart && {
        second_party_shipping_cents: Math.round((chosen.sellerAmount ?? 0) * 100),
      }),
      ...(isSwapCart && { deal_type: "swap" }),
      ...(leg1Cents != null && { leg1_quoted_cents: leg1Cents }),
      ...(leg11Cents != null && { leg1_1_quoted_cents: leg11Cents }),
      ...(leg2Cents != null && { leg2_quoted_cents: leg2Cents }),
      ...(leg21Cents != null && { leg2_1_quoted_cents: leg21Cents }),
      ...freightAuditFromChosenOption(chosen, isSwapCart),
    };
  }, [state.addresses, state.selectedAddressId, state.selectedShippingId, state.shippingOptions, state.cart, state.userName, state.userProfile, cartItemsToApi]);

  /** Called when user clicks "Continuar para Revisão" on shipping step. Patches linked order with current shipping so review shows correct freight, then navigates. */
  const handleContinueToReviewFromShipping = useCallback(async () => {
    if (state.cartOrderId != null) {
      const payload = buildOrderContentsPayload();
      if (payload) {
        try {
          await authClient.updateOrderContents(state.cartOrderId, payload);
        } catch {
          if (state.setCartOrderId) state.setCartOrderId(null);
        }
      }
    }
    state.setCurrentPage("checkout-review");
  }, [state.cartOrderId, state.setCurrentPage, state.setCartOrderId, buildOrderContentsPayload]);

  /** Order age (ms) beyond which we re-quote instead of using stored amounts. 20 minutes. */
  const ORDER_QUOTE_STALE_MS = 20 * 60 * 1000;

  /** Restore cart, address, and shipping from an existing order (for cartOrderId flow). If order is older than 20 minutes, clear cartOrderId and sync cart without order_id so user re-quotes and creates a new order on confirm. */
  const restoreCheckoutFromOrder = useCallback(
    async (orderId: string): Promise<void> => {
      const order = await authClient.getOrder(orderId);
      if ((order.status ?? "").toLowerCase() !== "pending_payment") {
        if (state.setCartOrderId) state.setCartOrderId(null);
        // Clear order_id on server cart so it does not keep pointing at a paid/cancelled order
        if (authClient.getAccessToken()) authClient.putCart(cartItemsToApi(state.cart)).catch(() => {});
        throw new Error("Este pedido já foi pago ou não está mais disponível.");
      }
      const createdAtMs = order.created_at ? new Date(order.created_at).getTime() : 0;
      const isStale = Number.isNaN(createdAtMs) || Date.now() - createdAtMs >= ORDER_QUOTE_STALE_MS;

      const lineItems = (order.line_items ?? []) as Array<{ ad_id?: string; quantity?: number; unit_price_cents?: number; offer_id?: string }>;
      const cartItems: CartItem[] = [];
      for (const line of lineItems) {
        const adId = line.ad_id;
        if (!adId) continue;
        try {
          const api = await authClient.getAdById(adId);
          const ad = authClient.apiAdToAd(api);
          const quantity = Math.max(1, line.quantity ?? 1);
          const offerPrice = line.unit_price_cents != null ? line.unit_price_cents / 100 : undefined;
          const hasOffer = !!line.offer_id;
          cartItems.push({
            ad,
            quantity,
            ...(offerPrice != null && { offerPrice }),
            ...(hasOffer && { offerId: line.offer_id, isOfferItem: true }),
          });
        } catch {
          // skip line if ad fetch fails
        }
      }
      state.setCart(cartItems);
      const snap = order.address_snapshot;
      const addrId = `order-${orderId}`;
      const addr: Address = {
        id: addrId,
        name: snap?.recipient_name?.trim() || "Endereço do pedido",
        street: snap?.street ?? "",
        number: snap?.number ?? "",
        complement: snap?.complement,
        neighborhood: snap?.neighborhood ?? "",
        city: snap?.city ?? "",
        state: snap?.state ?? "",
        zipCode: snap?.cep ?? "",
      };
      state.setAddresses((prev) => (prev.some((a) => a.id === addrId) ? prev : [addr, ...prev]));
      state.setSelectedAddressId(addrId);
      const cep = (snap?.cep ?? "").replace(/\D/g, "").slice(0, 8);
      const items = lineItems.filter((l) => l.ad_id).map((l) => ({ ad_id: l.ad_id!, quantity: Math.max(1, l.quantity ?? 1) }));
      if (cep.length >= 8 && items.length > 0) {
        const opts = await loadShippingOptions(cep, items, { cartOverride: cartItems });
        const match = opts?.find((o: { id: string }) => o.id === order.shipping_option_id);
        state.setSelectedShippingId(match?.id ?? opts?.[0]?.id ?? null);
      }

      if (isStale) {
        if (state.setCartOrderId) state.setCartOrderId(null);
        if (authClient.getAccessToken()) {
          authClient.putCart(cartItemsToApi(cartItems)).catch(() => {});
        }
      }
    },
    [
      state.setCart,
      state.setAddresses,
      state.setSelectedAddressId,
      state.setSelectedShippingId,
      state.setCartOrderId,
      loadShippingOptions,
      cartItemsToApi,
    ]
  );

  const handleSelectPayment = useCallback((id: string) => {
    state.setSelectedPaymentId(id);
  }, [state.setSelectedPaymentId]);

  const handleSelectInstallments = useCallback((installments: number) => {
    state.setSelectedInstallments(installments);
  }, [state.setSelectedInstallments]);

  const handleConfirmOrder = useCallback(() => {
    state.setOrderError(null);
    // Reuse existing order when user came back from Stripe payment page
    if (state.pendingPaymentOrderId && state.pendingPaymentClientSecret) {
      state.setCurrentPage("checkout-stripe");
      return;
    }
    // Pay existing order (cart tied to order from "Concluir pagamento" or restored cart)
    if (state.cartOrderId) {
      state.setOrderConfirming(true);
      authClient
        .getPaymentIntent(state.cartOrderId)
        .then((pi) => {
          const clientSecret = typeof pi.client_secret === "string" ? pi.client_secret : String(pi?.client_secret ?? "");
          const publishableKey = typeof pi.publishable_key === "string" ? pi.publishable_key : String(pi?.publishable_key ?? "");
          state.setOrderConfirming(false);
          state.setPendingPaymentOrderId(state.cartOrderId!);
          state.setPendingPaymentClientSecret(clientSecret || null);
          state.setPendingPaymentPublishableKey(publishableKey);
          state.setCurrentPage("checkout-stripe");
        })
        .catch((err) => {
          state.setOrderConfirming(false);
          const msg = (err as Error)?.message;
          state.setOrderError(typeof msg === "string" ? msg : "Não foi possível abrir o pagamento.");
        });
      return;
    }
    const selectedAddress = state.addresses.find((a) => a.id === state.selectedAddressId);
    const selectedShipping = state.shippingOptions.find((s) => s.id === state.selectedShippingId);
    if (!selectedAddress || !selectedShipping) {
      state.setOrderError("Selecione endereço e opção de envio.");
      return;
    }
    state.setOrderConfirming(true);
    const items = cartItemsToApi(state.cart);
    // Buyer name and phone are required; BFF returns 400 if either is missing.
    const recipientName = state.userName?.trim() ?? "";
    const recipientPhone = state.userProfile?.phone?.trim() ?? "";
    const address_snapshot: authClient.OrderAddressSnapshot = {
      cep: selectedAddress.zipCode,
      street: selectedAddress.street,
      number: selectedAddress.number,
      complement: selectedAddress.complement ?? "",
      neighborhood: selectedAddress.neighborhood,
      city: selectedAddress.city,
      state: selectedAddress.state,
      recipient_name: recipientName,
      recipient_phone: recipientPhone,
    };
    // Hub-based (swap) cart: set deal_type and second_party_shipping_cents so brian-may stores other party's amount for second PaymentIntent (P4: include 0).
    const isSwapCart = state.cart.some((it) => it.isOfferItem === true);
    const leg1Cents = selectedShipping.leg1 != null && selectedShipping.leg1 > 0 ? Math.round(selectedShipping.leg1 * 100) : undefined;
    const leg11Cents = selectedShipping.leg1_1 != null && selectedShipping.leg1_1 > 0 ? Math.round(selectedShipping.leg1_1 * 100) : undefined;
    const leg2Cents = selectedShipping.leg2 != null && selectedShipping.leg2 > 0 ? Math.round(selectedShipping.leg2 * 100) : undefined;
    const leg21Cents = selectedShipping.leg2_1 != null && selectedShipping.leg2_1 > 0 ? Math.round(selectedShipping.leg2_1 * 100) : undefined;
    authClient
      .createOrder({
        items,
        address_snapshot,
        shipping_option_id: selectedShipping.id,
        shipping_cents: Math.round(selectedShipping.price * 100),
        ...(isSwapCart && {
          second_party_shipping_cents: Math.round((selectedShipping.sellerAmount ?? 0) * 100),
        }),
        ...(isSwapCart && { deal_type: "swap" }),
        ...(leg1Cents != null && { leg1_quoted_cents: leg1Cents }),
        ...(leg11Cents != null && { leg1_1_quoted_cents: leg11Cents }),
        ...(leg2Cents != null && { leg2_quoted_cents: leg2Cents }),
        ...(leg21Cents != null && { leg2_1_quoted_cents: leg21Cents }),
        ...freightAuditFromChosenOption(selectedShipping, isSwapCart),
      })
      .then((res) => {
        state.setOrderError(null);
        // Persist order_id on server cart so resume works from other devices / after cleared storage (do not set cartOrderId in state yet to avoid a re-render that briefly shows review again)
        if (authClient.getAccessToken()) {
          authClient.putCart(cartItemsToApi(state.cart), res.order_id).catch(() => {});
        }
        return authClient.getPaymentIntent(res.order_id).then((pi) => {
          const orderIdStr = typeof res.order_id === "string" ? res.order_id : String(res?.order_id ?? "");
          const clientSecret = typeof pi.client_secret === "string" ? pi.client_secret : String(pi?.client_secret ?? "");
          const publishableKey = typeof pi.publishable_key === "string" ? pi.publishable_key : String(pi?.publishable_key ?? "");
          if (state.setCartOrderId) state.setCartOrderId(res.order_id);
          state.setPendingPaymentOrderId(orderIdStr || null);
          state.setPendingPaymentClientSecret(clientSecret || null);
          state.setPendingPaymentPublishableKey(publishableKey);
          state.setOrderConfirming(false);
          state.setCurrentPage("checkout-stripe");
        });
      })
      .catch((err) => {
        state.setOrderConfirming(false);
        const msg = (err as Error)?.message;
        state.setOrderError(typeof msg === "string" ? msg : "Não foi possível criar o pedido. Tente novamente.");
      });
  }, [
    state.addresses,
    state.cart,
    state.cartOrderId,
    state.selectedAddressId,
    state.selectedShippingId,
    state.shippingOptions,
    state.userName,
    state.userProfile,
    state.setCurrentPage,
    state.setOrderError,
    state.setOrderConfirming,
    state.setPendingPaymentOrderId,
    state.setPendingPaymentClientSecret,
    state.setPendingPaymentPublishableKey,
    state.setCartOrderId,
    cartItemsToApi,
  ]);

  /** Clears local + server cart and checkout linkage after any committed payment (full order or first leg of a swap). */
  const clearCartAndCheckoutLinks = useCallback(async () => {
    state.setCart([]);
    if (authClient.getAccessToken()) {
      await authClient.putCart([]).catch(() => {});
    }
    state.setPendingPaymentOrderId(null);
    state.setPendingPaymentClientSecret(null);
    state.setPendingPaymentPublishableKey("");
    if (state.setSecondPartyPaymentOrderId) state.setSecondPartyPaymentOrderId(null);
    if (state.setPayOrderId) state.setPayOrderId(null);
    if (state.setCartOrderId) state.setCartOrderId(null);
    state.setReturnToPage(null);
  }, [
    state.setCart,
    state.setReturnToPage,
    state.setPendingPaymentOrderId,
    state.setPendingPaymentClientSecret,
    state.setPendingPaymentPublishableKey,
    state.setSecondPartyPaymentOrderId,
    state.setPayOrderId,
    state.setCartOrderId,
  ]);

  const handlePaymentSuccess = useCallback(
    async (orderId: string) => {
      await clearCartAndCheckoutLinks();
      state.setLastOrderNumber(orderId);
      state.setCurrentPage("order-confirmation");
    },
    [clearCartAndCheckoutLinks, state.setLastOrderNumber, state.setCurrentPage]
  );

  const handleBackFromStripeCheckout = useCallback(() => {
    // Keep pending payment state so "Confirmar Pedido" reuses the same order
    state.setCurrentPage("checkout-review");
  }, [state.setCurrentPage]);

  const handlePayPendingOrder = useCallback(
    (orderId: string) => {
      return authClient
        .getPaymentIntent(orderId)
        .then((pi) => {
          const clientSecret = typeof pi.client_secret === "string" ? pi.client_secret : String(pi?.client_secret ?? "");
          const publishableKey = typeof pi.publishable_key === "string" ? pi.publishable_key : String(pi?.publishable_key ?? "");
          state.setPendingPaymentOrderId(orderId);
          state.setPendingPaymentClientSecret(clientSecret || null);
          state.setPendingPaymentPublishableKey(publishableKey);
          state.setCurrentPage("checkout-stripe");
        });
    },
    [
      state.setPendingPaymentOrderId,
      state.setPendingPaymentClientSecret,
      state.setPendingPaymentPublishableKey,
      state.setCurrentPage,
    ]
  );

  /** Go to checkout-review to pay an existing order: tie cart to order, restore cart/address/shipping from order, then show review. */
  const handleGoToCheckoutToPayOrder = useCallback(
    async (orderId: string) => {
      if (state.setCartOrderId) state.setCartOrderId(orderId);
      if (state.setPayOrderId) state.setPayOrderId(null);
      try {
        await restoreCheckoutFromOrder(orderId);
        state.setCurrentPage("checkout-review");
      } catch {
        if (state.setCartOrderId) state.setCartOrderId(null);
        throw new Error("Não foi possível carregar o pedido. Tente novamente.");
      }
    },
    [state.setCartOrderId, state.setPayOrderId, state.setCurrentPage, restoreCheckoutFromOrder]
  );

  /** From checkout-review in "pay order" mode: get payment intent and go to Stripe. */
  const handlePayOrderFromReview = useCallback(
    (orderId: string) => {
      if (state.setPayOrderId) state.setPayOrderId(null);
      return authClient
        .getPaymentIntent(orderId)
        .then((pi) => {
          const clientSecret = typeof pi.client_secret === "string" ? pi.client_secret : String(pi?.client_secret ?? "");
          const publishableKey = typeof pi.publishable_key === "string" ? pi.publishable_key : String(pi?.publishable_key ?? "");
          state.setPendingPaymentOrderId(orderId);
          state.setPendingPaymentClientSecret(clientSecret || null);
          state.setPendingPaymentPublishableKey(publishableKey);
          state.setCurrentPage("checkout-stripe");
        });
    },
    [
      state.setPayOrderId,
      state.setPendingPaymentOrderId,
      state.setPendingPaymentClientSecret,
      state.setPendingPaymentPublishableKey,
      state.setCurrentPage,
    ]
  );

  const handleChatWithSeller = useCallback(
    (adId: string, _adTitle: string, sellerId?: string) => {
      authClient
        .findOrCreateConversation({ ad_id: adId, seller_id: sellerId })
        .then((api) => {
          const conv = authClient.apiConversationToConversation(api, state.userId) as Conversation;
          state.setConversations((prev) => {
            const exists = prev.some((c) => c.id === conv.id);
            if (exists) return prev;
            return [conv, ...prev];
          });
          state.setSelectedConversationId(conv.id);
          state.setIsChatModalOpen(true);
        })
        .catch(() => {
          state.setIsChatModalOpen(true);
        });
    },
    [state.setConversations, state.setSelectedConversationId, state.setIsChatModalOpen, state.userId]
  );

  const handleSendMessage = useCallback((message: string) => {
    if (state.selectedConversationId) {
      state.setConversations(prev =>
        prev.map(conv =>
          conv.id === state.selectedConversationId
            ? { ...conv, lastMessage: message, timestamp: new Date() }
            : conv
        )
      );
    }
  }, [state.selectedConversationId, state.setConversations]);

  // Menu navigation helper - DRY principle
  const createMenuNavigationHandler = useCallback((page: PageType) => () => {
    state.setNavigatedFromMenu(true);
    if (navigation) {
      navigation.navigateToPage(page);
    } else {
      state.setCurrentPage(page);
    }
    state.setIsMenuOpen(false);
  }, [state.setNavigatedFromMenu, state.setCurrentPage, state.setIsMenuOpen, navigation]);

  const menuHandlers = useMemo(() => ({
    onLogout: () => {
      clearSessionAppRoute();
      if (authClient.isAuthConfigured()) void authClient.logout();
      state.setUserId("");
      state.setUserEmail("");
      state.setUserName("");
      state.setAuthEmailVerified(undefined);
      state.setUserProfile(null);
      state.setUserProfilePicture("");
      state.setNeedsOnboarding(false);
      state.setIsLoggedIn(false);
      state.setBrowsingAfterSignup(true);
      state.setFavoriteAdIds([]);
      state.setFavoriteCreatedAt({});
      state.setFavoriteItems([]);
      state.setCart([]);
      state.setAddresses([]);
      state.setSelectedAddressId(null);
      state.setShippingOptions([]);
      state.setSelectedShippingId(null);
      state.setHubDisplay(null);
      state.setConversations([]);
      state.setChatMessages([]);
      state.setSelectedConversationId(null);
      state.setNotificationUnreadCount(0);
      state.setIsChatModalOpen(false);
      state.setNavigationHistory([]);
      state.setReceivedOffers([]);
      state.setSentOffers([]);
      state.setReturnToPage(null);
      if (state.setCartOrderId) state.setCartOrderId(null);
      state.setCurrentPage("home");
      state.setIsMenuOpen(false);
      state.setNavigatedFromMenu(false);
    },
    onNavigateToNotifications: createMenuNavigationHandler("notifications"),
    onNavigateToAddresses: createMenuNavigationHandler("addresses"),
    onNavigateToMyAds: createMenuNavigationHandler("my-ads"),
    onNavigateToFavorites: createMenuNavigationHandler("favorites"),
    onNavigateToChat: () => {
      state.setSelectedConversationId(null);
      state.setIsChatModalOpen(true);
      state.setIsMenuOpen(false);
    },
    onNavigateToProfile: createMenuNavigationHandler("profile"),
    onNavigateToSettings: createMenuNavigationHandler("settings"),
    onNavigateToHelp: createMenuNavigationHandler("help"),
    onNavigateToReceivedOffers: createMenuNavigationHandler("received-offers"),
    onNavigateToSentOffers: createMenuNavigationHandler("sent-offers"),
    onNavigateToSales: createMenuNavigationHandler("sales"),
    onNavigateToPurchases: createMenuNavigationHandler("purchases"),
    onNavigateToWallet: createMenuNavigationHandler("wallet"),
    setSelectedSellerName: state.setSelectedSellerName,
    setSelectedSellerId: state.setSelectedSellerId,
  }), [
    createMenuNavigationHandler,
    state.setIsLoggedIn,
    state.setUserId,
    state.setUserEmail,
    state.setUserName,
    state.setAuthEmailVerified,
    state.setUserProfile,
    state.setUserProfilePicture,
    state.setNeedsOnboarding,
    state.setFavoriteAdIds,
    state.setFavoriteCreatedAt,
    state.setFavoriteItems,
    state.setCart,
    state.setAddresses,
    state.setSelectedAddressId,
    state.setShippingOptions,
    state.setSelectedShippingId,
    state.setHubDisplay,
    state.setConversations,
    state.setChatMessages,
    state.setSelectedConversationId,
    state.setNotificationUnreadCount,
    state.setIsChatModalOpen,
    state.setNavigationHistory,
    state.setReceivedOffers,
    state.setSentOffers,
    state.setReturnToPage,
    state.setCartOrderId,
    state.setBrowsingAfterSignup,
    state.setCurrentPage,
    state.setIsMenuOpen,
    state.setNavigatedFromMenu,
    state.setSelectedSellerName,
    state.setSelectedSellerId,
  ]);

  return {
    handleCreateAd,
    handleToggleFavorite,
    handleRemoveFavorite,
    isFavorite,
    handleAddToCart,
    handleRemoveFromCart,
    isInCart,
    handleUpdateCartQuantity,
    handleAddNewAddress,
    handleAddNewPaymentMethod,
    handleSelectAddress,
    handleSelectShipping,
    handleContinueToReviewFromShipping,
    loadShippingOptions,
    handleSelectPayment,
    handleSelectInstallments,
    handleConfirmOrder,
    clearCartAndCheckoutLinks,
    handlePaymentSuccess,
    handleBackFromStripeCheckout,
    handlePayPendingOrder,
    handleGoToCheckoutToPayOrder,
    handlePayOrderFromReview,
    restoreCheckoutFromOrder,
    handleChatWithSeller,
    handleSendMessage,
    menuHandlers,
  };
}